import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiService, graphBuilderApi } from '../../../services/api';
import { getGraphBuilderBaseUrl } from '../../../utils/env';
import AgentChatThread from './AgentChatThread';
import './GraphAgent.css';

const GRAPH_BUILDER_BASE_URL = getGraphBuilderBaseUrl();

const SUGGESTED_PROMPTS = [
  'Show me a summary of this graph',
  'What are the most connected nodes in this graph?',
  'Run a test query and explain the result',
  'Find orphan nodes that have no connections',
];

function GraphAgentPanel({ graphId, onClose, onGraphChanged }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [pendingChanges, setPendingChanges] = useState([]);
  const [acceptedChanges, setAcceptedChanges] = useState(new Set());
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [accountId, setAccountId] = useState('');
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    apiService.listCodingLLMProviders()
      .then((data) => {
        const provs = data.providers || [];
        setProviders(provs);
        const defaultProv = provs.find((p) => p.is_default) || provs[0];
        if (defaultProv) setSelectedProviderId(defaultProv.id);
      })
      .catch(() => {});

    try {
      const raw = localStorage.getItem('userData') || localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        setAccountId(u?.account_id || u?.AccountId || u?.accountId || '');
      }
    } catch {}
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, [messages]);

  const buildConversationHistory = useCallback(() => {
    return messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content || '' }));
  }, [messages]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || isStreaming) return;

    const userMsg = { role: 'user', content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsStreaming(true);
    setStreamingText('');

    const conversationHistory = buildConversationHistory();
    const assistantMsg = {
      role: 'assistant',
      content: '',
      toolCalls: [],
      proposals: [],
      simulations: [],
    };

    try {
      const url = `${GRAPH_BUILDER_BASE_URL}/api/graph-agent/chat`;
      const body = {
        graph_id: graphId,
        message: text.trim(),
        conversation_history: conversationHistory,
        coding_llm_provider_id: selectedProviderId || undefined,
        account_id: accountId || undefined,
        pending_changes: pendingChanges,
        stream: true,
      };

      const controller = new AbortController();
      abortRef.current = controller;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';
      const toolCalls = [];
      const proposals = [];
      const simulations = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          let event;
          try {
            event = JSON.parse(jsonStr);
          } catch {
            continue;
          }

          if (event.type === 'token') {
            fullText += event.content;
            setStreamingText(fullText);
          } else if (event.type === 'text') {
            fullText = event.content;
            setStreamingText(fullText);
          } else if (event.type === 'tool_call') {
            toolCalls.push({ toolName: event.tool_name, toolCallId: event.tool_call_id });
          } else if (event.type === 'change_proposal') {
            proposals.push(event.proposal);
            setPendingChanges((prev) => [...prev, event.proposal]);
          } else if (event.type === 'tool_result') {
            if (event.tool_name === 'run_simulation' || event.tool_name === 'compare_results') {
              try {
                const parsed = JSON.parse(event.result_preview.replace(/\.\.\.$/,''));
                simulations.push({ ...parsed, _label: event.tool_name === 'compare_results' ? 'Before' : undefined });
              } catch {}
            }
          } else if (event.type === 'pending_changes_summary') {
            // handled via pendingChanges state
          } else if (event.type === 'error') {
            fullText += `\n\n**Error:** ${event.message}`;
            setStreamingText(fullText);
          } else if (event.type === 'done') {
            break;
          }
        }
      }

      assistantMsg.content = fullText;
      assistantMsg.toolCalls = toolCalls;
      assistantMsg.proposals = proposals;
      assistantMsg.simulations = simulations;

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      if (err.name === 'AbortError') return;
      assistantMsg.content = `**Error:** ${err.message}`;
      setMessages((prev) => [...prev, assistantMsg]);
    } finally {
      setIsStreaming(false);
      setStreamingText('');
      abortRef.current = null;
    }
  }, [graphId, isStreaming, selectedProviderId, accountId, pendingChanges, buildConversationHistory]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleAcceptChange = (msgIdx, proposalIdx) => {
    setAcceptedChanges((prev) => new Set(prev).add(`${msgIdx}-${proposalIdx}`));
  };

  const handleRejectChange = (msgIdx, proposalIdx) => {
    setAcceptedChanges((prev) => {
      const next = new Set(prev);
      next.delete(`${msgIdx}-${proposalIdx}`);
      return next;
    });
    // Remove from pending
    const msg = messages[msgIdx];
    if (msg?.proposals?.[proposalIdx]) {
      const rejected = msg.proposals[proposalIdx];
      setPendingChanges((prev) => prev.filter((c) => c !== rejected));
    }
  };

  const handleApplyAll = async () => {
    if (!pendingChanges.length) return;
    try {
      const url = `${GRAPH_BUILDER_BASE_URL}/api/graph-agent/apply`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ graph_id: graphId, changes: pendingChanges }),
      });
      const result = await resp.json();
      const appliedCount = result.applied?.length || 0;
      const errorCount = result.errors?.length || 0;

      setMessages((prev) => [
        ...prev,
        {
          role: 'system',
          content: `Applied ${appliedCount} change(s)${errorCount ? `, ${errorCount} error(s)` : ''}. Tables refreshed.`,
        },
      ]);
      setPendingChanges([]);
      setAcceptedChanges(new Set());
      if (onGraphChanged) onGraphChanged();
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'system', content: `Failed to apply changes: ${err.message}` },
      ]);
    }
  };

  const handleDiscard = () => {
    setPendingChanges([]);
    setAcceptedChanges(new Set());
    setMessages((prev) => [
      ...prev,
      { role: 'system', content: 'All pending changes discarded.' },
    ]);
  };

  const showWelcome = messages.length === 0;

  return (
    <div className="graph-agent-overlay">
      {/* Header */}
      <div className="graph-agent-header">
        <h2 className="graph-agent-header__title">
          <span className="graph-agent-header__title-icon">AI</span>
          Graph Agent
        </h2>
        {providers.length > 0 && (
          <select
            className="graph-agent-header__model-select"
            value={selectedProviderId}
            onChange={(e) => setSelectedProviderId(e.target.value)}
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.model_name})
              </option>
            ))}
          </select>
        )}
        <button className="graph-agent-header__close" onClick={onClose} title="Close">
          ×
        </button>
      </div>

      {/* Messages or Welcome */}
      {showWelcome ? (
        <div className="graph-agent-welcome">
          <div className="graph-agent-welcome__icon">AI</div>
          <h3>Graph Agent</h3>
          <p>
            I can help you understand, diagnose, and edit this graph.
            Ask me to search nodes, trace issues, create connections, or run test queries.
          </p>
          <div className="graph-agent-welcome__suggestions">
            {SUGGESTED_PROMPTS.map((prompt, i) => (
              <button
                key={i}
                className="graph-agent-welcome__suggestion"
                onClick={() => sendMessage(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <AgentChatThread
          messages={messages}
          isStreaming={isStreaming}
          streamingText={streamingText}
          onAcceptChange={handleAcceptChange}
          onRejectChange={handleRejectChange}
          acceptedChanges={acceptedChanges}
        />
      )}

      {/* Input Area */}
      <div className="graph-agent-input">
        {pendingChanges.length > 0 && (
          <div className="graph-agent-input__pending-bar">
            <span>{pendingChanges.length} pending change(s)</span>
            <button className="btn-discard" onClick={handleDiscard}>Discard</button>
            <button className="btn-apply-all" onClick={handleApplyAll}>Apply All</button>
          </div>
        )}
        <div className="graph-agent-input__row">
          <textarea
            ref={inputRef}
            className="graph-agent-input__textarea"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={providers.length === 0
              ? 'Configure a Coding LLM provider in Settings first...'
              : 'Ask the agent to inspect, diagnose, or edit the graph...'
            }
            disabled={isStreaming || providers.length === 0}
            rows={1}
          />
          <button
            className="graph-agent-input__send"
            onClick={() => sendMessage(inputValue)}
            disabled={isStreaming || !inputValue.trim() || providers.length === 0}
            title="Send"
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}

export default GraphAgentPanel;
